import "./UpsComponent.scss";
import React, { useEffect, useState } from "react";
import { Button, DatePicker, Input, InputGroup, Message, Notification, toaster, Toggle, Whisper } from "rsuite";
import { UpsApi } from "../../../../../services/Ups.api";
import { Reload } from "@rsuite/icons";
import { extractTimezoneFromISO, parseTimeWithMoment, prepareTimeData } from "../Dimmer/utils/Dimmer.utils";
import RebootConfirmationModal from "../Dimmer/components/RebootConfirmationModal";
import { SharedApi } from "../../../../../services/Shared.api";
import { getTooltip } from "../../../../../shared/Tooltip";
import UspInfoSection from "./components/UspInfoSection";
import StatusControl from "../../../../shared/StatusControl/StatusControl";
import {TfiSaveIcon} from "../../../../../models/Icons.model";
import {UpsComponentProps, UpsData, UpsParams} from "./Ups.model";

const UpsComponent = ({ upsId, isAdmin }: UpsComponentProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [upsParams, setUpsParams] = useState<UpsParams>({});
  const [upsData, setUpsData] = useState<UpsData>({});
  const [upsTime, setUpsTime] = useState<Date | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>("+3");
  const [showRebootModal, setShowRebootModal] = useState<boolean>(false);

  const successMessage = (
    <Notification type="success" closable>
      <p>Настройки сохранены</p>
    </Notification>
  );

  const errorMessage = (
    <Notification type="error" closable>
      <p>Ошибка</p>
    </Notification>
  );

  const successRebootMessage = (
    <Notification type="success" closable>
      <p>Устройство перезапускается</p>
    </Notification>
  );

  const errorRebootMessage = (
    <Notification type="error" closable>
      <p>Ошибка при перезапуске устройства</p>
    </Notification>
  );

  useEffect(() => {
    if (upsId !== null && upsId !== undefined) {
      getUpsParams().then();
      getTime().then();
      getUspData().then();
    }
  }, [upsId]);

  const getUpsParams = async () => {
    setLoading(true);
    try {
      const result = await UpsApi.getUpsParams(upsId as string);

      setUpsParams(result);
    } catch (error) {
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const getUspData = async () => {
    try {
      const result = await SharedApi.getDeviceInfo(String(upsId));
      setUpsData(result);
    } catch (e) {
      toaster.push(
        <Message type="error" closable>
          Ошибка при загрузке данных о ИБП
        </Message>,
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onOutputDisable = async () => {
    setLoading(true);
    try {
      const result = await UpsApi.outputDisable(upsId as string);

      if (result) {
        getUpsParams().then();
        toaster.push(successMessage, { placement: "topEnd" });
      }
    } catch (error) {
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const onSaveTime = async () => {
    setLoading(true);
    try {
      const timeData = prepareTimeData(upsTime, userTimezone);

      await UpsApi.saveTime(upsId as string, timeData as any);
      await getTime();

      toaster.push(successMessage, { placement: "topEnd" });
    } catch (e) {
      console.error("Ошибка при сохранении времени:", e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const getTime = async () => {
    setLoading(true);

    try {
      const data = await UpsApi.getTime(upsId as string);
      if (data.time) {
        const timezone = extractTimezoneFromISO(data.time);
        setUserTimezone(timezone);

        const newDeviceTime = parseTimeWithMoment(data.time);
        setUpsTime(newDeviceTime);
      }
    } catch (e) {
      console.error("Ошибка получения данных:", e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const handleOutputToggle = async (enabled: boolean) => {
    setLoading(true);

    try {
      const result = await UpsApi.setOutputState(upsId as string, enabled);

      if (result) {
        getUpsParams().then();

        toaster.push(successMessage, { placement: "topEnd" });
      } else {
        throw new Error("Не удалось изменить состояние выхода");
      }
    } catch (error) {
      console.error("Ошибка при изменении состояния выхода:", error);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRebootModal = () => {
    setShowRebootModal(true);
  };

  const handleCloseRebootModal = () => {
    setShowRebootModal(false);
  };

  const handleConfirmReboot = async () => {
    setLoading(true);
    setShowRebootModal(false);

    try {
      const response = await SharedApi.rebootDevice(upsId as string);

      if (response) {
        toaster.push(successRebootMessage, { placement: "topEnd" });
      }
    } catch (error) {
      toaster.push(errorRebootMessage, { placement: "topEnd" });
      console.error("Ошибка при перезапуске устройства:", error);
    }
  };

  const onChangePlace = (value: string) => {
    setUpsData((prev) => ({
      ...prev,
      place: value
    }));
  };

  const onChangeArticle = (value: string) => {
    setUpsData((prev) => ({
      ...prev,
      article: value
    }));
  };

  const onChangeModel = (value: string) => {
    setUpsData((prev) => ({
      ...prev,
      model: value
    }));
  };

  const onSavePlace = async () => {
    setLoading(true);
    try {
      await SharedApi.updatePlace(upsId as string, upsData?.place ?? "");
      toaster.push(successMessage, { placement: "topEnd" });
      await getUspData();
    } catch (e) {
      console.error("Ошибка при сохранении места:", e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const onSaveArticle = async () => {
    setLoading(true);
    try {
      await SharedApi.updateArticle(upsId as string, upsData?.article ?? "");
      toaster.push(successMessage, { placement: "topEnd" });
      await getUspData();
    } catch (e) {
      console.error("Ошибка при сохранении места:", e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  const onSaveModel = async () => {
    setLoading(true);
    try {
      await SharedApi.updateModel(upsId as string, upsData?.model ?? "");
      toaster.push(successMessage, { placement: "topEnd" });
      await getUspData();
    } catch (e) {
      console.error("Ошибка при сохранении места:", e);
      toaster.push(errorMessage, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="row mb-2">
        <div className="col-md-6 text-center">
          <Toggle
            color="blue"
            checkedChildren="ON"
            unCheckedChildren="OFF"
            checked={upsParams?.outputEnabled}
            loading={loading}
            disabled={loading}
            onChange={handleOutputToggle}
          >
            <span>Состояние выхода ИБП (включен/выключен)</span>
          </Toggle>
        </div>
        <div className="col-md-6 text-center d-flex align-items-center">
          <StatusControl status={upsParams?.externalPowerEnabled ? "online" : "offline"} />
          <p>Наличие внешней сети 220В (да/нет)</p>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <InputGroup className="mb-2">
            <InputGroup.Addon style={{ width: 130 }}>Время и дата</InputGroup.Addon>
            <DatePicker
              style={{ width: "100%" }}
              loading={loading}
              value={upsTime}
              onChange={setUpsTime}
              format="dd.MM.yyyy HH:mm"
              cleanable={false}
            />
            <InputGroup.Addon>
              <TfiSaveIcon
                style={{ cursor: "pointer" }}
                onClick={() => onSaveTime()}
              />
            </InputGroup.Addon>
            <InputGroup.Addon>
              <Reload
                style={{ cursor: "pointer" }}
                onClick={() => getTime().then()}
              />
            </InputGroup.Addon>
          </InputGroup>
          <InputGroup className="mb-2">
            <InputGroup.Addon>Выходное напряжение</InputGroup.Addon>
            <Input
              size="md"
              value={upsParams?.outputVoltage || ''}
            />
          </InputGroup>
          <InputGroup className="mb-2">
            <InputGroup.Addon>Выходной ток</InputGroup.Addon>
            <Input
              size="md"
              value={upsParams?.outputCurrent || ''}
            />
          </InputGroup>
        </div>
        <div className="col-md-6">
          <InputGroup className="mb-2">
            <InputGroup.Addon>Напряжение на аккумуляторной батарее</InputGroup.Addon>
            <Input
              size="md"
              value={upsParams?.batteryVoltage || ''}
            />
          </InputGroup>
          <InputGroup className="mb-2">
            <InputGroup.Addon>Ток заряда аккумулятора</InputGroup.Addon>
            <Input
              size="md"
              value={upsParams?.batteryChargeCurrent || ''}
            />
          </InputGroup>
          <InputGroup className="mb-2">
            <InputGroup.Addon>Напряжение от сетевого блока питания</InputGroup.Addon>
            <Input
              size="md"
              value={upsParams?.powerSupplyVoltage || ''}
            />
          </InputGroup>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="row">
            <div className="col-md-6">
              <Whisper
                placement="top"
                controlId="control-id-hover"
                trigger="hover"
                speaker={getTooltip("ОТКЛЮЧЕНИЕ ИБП на 1 минуту")}
              >
                <Button
                  style={{ width: "100%" }}
                  appearance="primary"
                  onClick={onOutputDisable}
                >
                  ОТКЛЮЧЕНИЕ ИБП
                </Button>
              </Whisper>
            </div>
            <div className="col-md-6">
              <InputGroup style={{ width: "100%" }}>
                <InputGroup.Addon>Время до включения</InputGroup.Addon>
                <Input
                  size="md"
                  value={upsParams?.enableCountdown || 0}
                />
              </InputGroup>
            </div>
          </div>
        </div>
        <div className="col-md-6 text-center align-items-center justify-content-center d-flex">
          <Button
            type="button"
            className="reload-btn"
            onClick={handleOpenRebootModal}
            loading={loading}
            disabled={loading}
          >
            Перезапустить устройство
          </Button>
        </div>
      </div>

      <div className="row mt-2 mb-2">
        <div className="col-md-12">
          <UspInfoSection
            deviceData={upsData}
            deviceId={String(upsId)}
            loading={loading}
            onChangePlace={onChangePlace}
            onChangeArticle={onChangeArticle}
            onChangeModel={onChangeModel}
            onSavePlace={onSavePlace}
            onSaveArticle={onSaveArticle}
            onSaveModel={onSaveModel}
            onRefreshDeviceInfo={getUspData}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      <RebootConfirmationModal
        show={showRebootModal}
        onHide={handleCloseRebootModal}
        onConfirm={handleConfirmReboot}
      />
    </>
  );
};

export default UpsComponent;
